import { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { IconReportSearch } from '@tabler/icons-react';

const SearchField = ({
  placeholder,
  width = 355,
  highth,
  height,
  mb = 3,
  searchTerm = '',
  searchReports,
  reportType,
  labelOptions = [],
  fallbackOptions = [],
  normalizeReportType
}) => {
  const [isTypingState, setIsTypingState] = useState(false);

  const reportTypeLabel = useMemo(() => {
    if (placeholder) return '';

    const selectedType = reportType || '';
    const desktopSelected = labelOptions.find((option) => !option?.isDivider && option?.value === selectedType)?.label;
    if (desktopSelected) {
      return desktopSelected.trim();
    }

    const directMatch = fallbackOptions.find((option) => option?.val === selectedType)?.displayText;
    if (directMatch) {
      return directMatch;
    }

    const normalizedValue = typeof normalizeReportType === 'function' ? normalizeReportType(selectedType) : selectedType;
    const normalizedMatch = fallbackOptions.find((option) => option?.val === normalizedValue)?.displayText;
    if (normalizedMatch) {
      return normalizedMatch;
    }

    return selectedType || 'Reports';
  }, [placeholder, reportType, labelOptions, fallbackOptions, normalizeReportType]);

  const computedPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    return /reports?$/i.test(reportTypeLabel) ? `Search ${reportTypeLabel}` : `Search ${reportTypeLabel} Reports`;
  }, [placeholder, reportTypeLabel]);

  const typingHelperText = useMemo(() => {
    if (placeholder) {
      if (/^search\s+/i.test(placeholder)) {
        return placeholder.replace(/^search\s+/i, 'Type to search ');
      }
      return `Type to search ${placeholder}`;
    }
    return /reports?$/i.test(reportTypeLabel) ? `Type to search ${reportTypeLabel}` : `Type to search ${reportTypeLabel} Reports`;
  }, [placeholder, reportTypeLabel]);

  const displayPlaceholder = isTypingState ? '' : computedPlaceholder;

  const handleChange = (event) => {
    if (typeof searchReports === 'function') {
      searchReports(event);
      return;
    }

    if (typeof searchReports?.onChange === 'function') {
      searchReports.onChange(event);
      return;
    }

    if (typeof searchReports?.setSearchTerm === 'function') {
      searchReports.setSearchTerm(event.target.value);
    }
  };

  const handleClick = (event) => {
    setIsTypingState(true);

    if (typeof searchReports?.onClick === 'function') {
      searchReports.onClick(event);
      return;
    }

    if (typeof searchReports?.setAnchorEl === 'function') {
      searchReports.setAnchorEl(event.currentTarget);
    }
  };

  const handleFocus = () => setIsTypingState(true);

  const handleBlur = () => setIsTypingState(false);

  return (
    <TextField
      placeholder={displayPlaceholder}
      variant="outlined"
      value={searchTerm}
      onChange={handleChange}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      helperText={isTypingState ? typingHelperText : ''}
      sx={{
        position: 'relative',
        width,
        mb,
        '& .MuiOutlinedInput-root': {
          borderRadius: isTypingState ? '16px 16px 0 0' : '16px',
          height: highth || height || 44,
          backgroundColor: '#FFFFFF',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '1px',
          paddingBottom: '1px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#a2a3a3',
          borderWidth: 1,
          borderBottomLeftRadius: isTypingState ? 0 : '16px',
          borderBottomRightRadius: isTypingState ? 0 : '16px'
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#068DF1',
          borderWidth: "2px"
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#068DF1'
        },
        '& .MuiOutlinedInput-input': {
          fontFamily: 'Noto Sans',
          fontSize: 14,
          color: '#111111',
          opacity: 1
        },
        '& .MuiInputAdornment-root': { ml: 0 },
        '& .MuiInputBase-input::placeholder': {
          color: '#111111',
          opacity: 1
        },
        '& .MuiFormHelperText-root': {
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          margin: 0,
          padding: '10px 16px',
          fontFamily: 'Noto Sans',
          fontSize: 14,
          color: '#111111',
          lineHeight: '20px',
          borderRadius: '0 0 16px 16px',
          borderWidth: 2,
          borderStyle: 'solid',
          borderTopWidth: 0,
          borderColor: '#068DF1',
          backgroundColor: '#FFFFFF',
          zIndex: 10
        }
      }}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ ml: 0, mr: 1.5 }}>
            <IconReportSearch stroke={2} color="#001A47" size={20} />
          </InputAdornment>
        ),
        readOnly: !!searchReports?.readOnly
      }}
    />
  );
};

export default SearchField;